import React from "react";
import { toast } from "sonner";
import { http } from "@/lib/api";
import { ADMIN } from "@/constants/testIds";
import { LogOut } from "lucide-react";

export default function Admin() {
  const [token, setToken] = React.useState(() => localStorage.getItem("he_admin_token"));
  const [tab, setTab] = React.useState("courses");
  const [data, setData] = React.useState({ courses: [], teachers: [], locations: [], events: [], contacts: [], inscriptions: [], disciplines: [], formulas: [], news: [], settings: { texts: {}, images: {} }, partners: [], gallery: [] });

  const loadAll = React.useCallback(async () => {
    try {
      const [c, t, l, e, ct, ins, d, f, n, s, p, g] = await Promise.all([
        http.get("/courses"), http.get("/teachers"), http.get("/locations"),
        http.get("/events"), http.get("/admin/contacts"), http.get("/admin/inscriptions"),
        http.get("/admin/disciplines"), http.get("/formulas"), http.get("/admin/news"), http.get("/settings"),
        http.get("/admin/partners"), http.get("/admin/gallery"),
      ]);
      setData({ courses: c.data, teachers: t.data, locations: l.data, events: e.data, contacts: ct.data, inscriptions: ins.data, disciplines: d.data, formulas: f.data, news: n.data, settings: s.data || { texts: {}, images: {} }, partners: p.data, gallery: g.data });
    } catch { /* ignore */ }
  }, []);

  React.useEffect(() => { if (token) loadAll(); }, [token, loadAll]);

  if (!token) return <Login onSuccess={(tk) => { localStorage.setItem("he_admin_token", tk); setToken(tk); }} />;

  const logout = () => { localStorage.removeItem("he_admin_token"); setToken(null); };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16">
      <div className="flex items-center justify-between">
        <div>
          <p className="he-overline text-neutral-500">Espace admin</p>
          <h1 className="font-heading text-4xl font-black tracking-tighter mt-2">Dashboard</h1>
        </div>
        <button onClick={logout} data-testid={ADMIN.logout} className="text-sm inline-flex items-center gap-1 text-neutral-600 hover:text-[var(--he-red)]"><LogOut className="w-4 h-4" /> Déconnexion</button>
      </div>

      <div className="mt-8 flex gap-1 border-b border-black/10 overflow-x-auto no-scrollbar">
        {[
          ["courses", "Cours", ADMIN.tabCourses],
          ["disciplines", "Disciplines", ADMIN.tabDisciplines],
          ["teachers", "Professeurs", ADMIN.tabTeachers],
          ["formulas", "Formules", ADMIN.tabFormulas],
          ["locations", "Lieux", ADMIN.tabLocations],
          ["events", "Événements", ADMIN.tabEvents],
          ["news", "Actualités", ADMIN.tabNews],
          ["partners", "Partenaires", ADMIN.tabPartners],
          ["gallery", "Galerie", ADMIN.tabGallery],
          ["settings", "Textes & visuels", ADMIN.tabSettings],
          ["contacts", "Contacts", ADMIN.tabContacts],
          ["inscriptions", "Inscriptions", ADMIN.tabInscriptions],
        ].map(([k, label, tid]) => (
          <button key={k} data-testid={tid} onClick={() => setTab(k)}
            className={`px-4 py-2 text-sm border-b-2 -mb-px transition-colors ${tab === k ? "border-[var(--he-red)] text-[var(--he-red)]" : "border-transparent text-neutral-600 hover:text-black"}`}>
            {label}
          </button>
        ))}
      </div>

      <div className="mt-8">
        {tab === "courses" && <CoursesAdmin data={data.courses} reload={loadAll} teachers={data.teachers} />}
        {tab === "disciplines" && <DisciplinesAdmin data={data.disciplines} reload={loadAll} />}
        {tab === "teachers" && <TeachersAdmin data={data.teachers} reload={loadAll} />}
        {tab === "formulas" && <FormulasAdmin data={data.formulas} reload={loadAll} />}
        {tab === "locations" && <LocationsAdmin data={data.locations} reload={loadAll} />}
        {tab === "events" && <EventsAdmin data={data.events} reload={loadAll} />}
        {tab === "news" && <NewsAdmin data={data.news} reload={loadAll} />}
        {tab === "partners" && <PartnersAdmin data={data.partners} reload={loadAll} />}
        {tab === "gallery" && <GalleryAdmin data={data.gallery} reload={loadAll} />}
        {tab === "settings" && <SettingsAdmin settings={data.settings} reload={loadAll} />}
        {tab === "contacts" && <ReadOnlyList items={data.contacts} fields={["created_at", "pole", "subject", "first_name", "last_name", "email", "phone", "message"]} />}
        {tab === "inscriptions" && <ReadOnlyList items={data.inscriptions} fields={["created_at", "first_name", "last_name", "email", "phone", "city", "disciplines", "level", "message"]} />}
      </div>
    </div>
  );
}

function Login({ onSuccess }) {
  const [email, setEmail] = React.useState("");
  const [pw, setPw] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      const r = await http.post("/admin/login", { email, password: pw });
      onSuccess(r.data.token);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Identifiants invalides");
    } finally { setLoading(false); }
  };
  return (
    <div className="max-w-md mx-auto px-4 py-24">
      <h1 className="font-heading text-4xl font-black tracking-tighter">Admin</h1>
      <p className="mt-3 text-sm text-neutral-600 font-body">Espace réservé à l’administration Heel’Events.</p>
      <form data-testid={ADMIN.loginForm} onSubmit={submit} className="mt-8 space-y-4">
        <label className="flex flex-col">
          <span className="he-overline text-neutral-500 mb-1.5">Email</span>
          <input required type="email" data-testid={ADMIN.loginEmail} value={email} onChange={e => setEmail(e.target.value)} className={inp} />
        </label>
        <label className="flex flex-col">
          <span className="he-overline text-neutral-500 mb-1.5">Mot de passe</span>
          <input required type="password" data-testid={ADMIN.loginPassword} value={pw} onChange={e => setPw(e.target.value)} className={inp} />
        </label>
        <button data-testid={ADMIN.loginSubmit} disabled={loading} className="btn-red rounded-full px-6 py-3 text-sm w-full disabled:opacity-60">
          {loading ? "Connexion..." : "Se connecter"}
        </button>
      </form>
    </div>
  );
}

const inp = "border border-black/15 rounded-lg px-3 py-2.5 text-sm w-full bg-white focus:outline-none focus:ring-2 focus:ring-[var(--he-red)]/40";

function CoursesAdmin({ data, teachers, reload }) {
  const empty = { name: "", category: "afro", audience: "adultes", level: "tous niveaux", teacher: "", city: "Fresnes", location: "", day: "Lundi", start: "19:00", end: "20:00", description: "", image: "", status: "open" };
  const [form, setForm] = React.useState(empty);
  const [editing, setEditing] = React.useState(null);
  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));
  const startEdit = (c) => { setEditing(c.id); setForm({ ...empty, ...c, teacher: c.teacher || "" }); };
  const cancel = () => { setEditing(null); setForm(empty); };
  const save = async (e) => {
    e.preventDefault();
    const body = { ...form, teacher: form.teacher || null };
    try {
      if (editing) await http.put(`/admin/courses/${editing}`, body);
      else await http.post("/admin/courses", body);
      toast.success("Enregistré"); cancel(); reload();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };
  const del = async (id) => {
    if (!window.confirm("Supprimer ce cours ?")) return;
    try { await http.delete(`/admin/courses/${id}`); toast.success("Supprimé"); reload(); }
    catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };
  return (
    <div>
      <form onSubmit={save} className="grid grid-cols-2 lg:grid-cols-6 gap-2 mb-6">
        <input placeholder="Nom" value={form.name} onChange={set("name")} className={inp} required />
        <input placeholder="Ville" value={form.city} onChange={set("city")} className={inp} required />
        <input placeholder="Lieu" value={form.location || ""} onChange={set("location")} className={inp} />
        <select value={form.day} onChange={set("day")} className={inp}>{["Lundi","Mardi","Mercredi","Jeudi","Vendredi","Samedi","Dimanche"].map(d=><option key={d}>{d}</option>)}</select>
        <input placeholder="19:00" value={form.start} onChange={set("start")} className={inp} required />
        <input placeholder="20:00" value={form.end} onChange={set("end")} className={inp} required />
        <select value={form.category} onChange={set("category")} className={inp}>{["afro","couple","urban","oriental","heels","fitness","kids"].map(c=><option key={c}>{c}</option>)}</select>
        <select value={form.audience} onChange={set("audience")} className={inp}>{["enfants","ados","adultes"].map(a=><option key={a}>{a}</option>)}</select>
        <select value={form.level} onChange={set("level")} className={inp}>{["débutant","intermédiaire","avancé","tous niveaux"].map(l=><option key={l}>{l}</option>)}</select>
        <select value={form.teacher} onChange={set("teacher")} className={inp}>
          <option value="">— Prof —</option>
          {teachers.map(t => <option key={t.id} value={t.first_name}>{t.first_name}</option>)}
        </select>
        <select value={form.status} onChange={set("status")} className={inp}>{["open","complet","terminé"].map(s=><option key={s}>{s}</option>)}</select>
        <input placeholder="Affiche du cours (URL, optionnel)" value={form.image || ""} onChange={set("image")} className={inp} />
        <div className="flex gap-2">
          <button className="btn-red rounded-full px-4 py-2 text-xs flex-1">{editing ? "Modifier" : "Ajouter"}</button>
          {editing && <button type="button" onClick={cancel} className="rounded-full px-3 py-2 text-xs border border-black/15">Annuler</button>}
        </div>
      </form>

      <Table
        cols={["Nom", "Ville", "Jour", "Créneau", "Prof", "Niveau", "Statut", ""]}
        rows={data.map(c => [
          c.name, c.city, c.day, `${c.start}-${c.end}`, c.teacher || "—", c.level, c.status,
          <div key={`a-${c.id}`} className="flex gap-2">
            <button onClick={() => startEdit(c)} className="text-xs underline">Éditer</button>
            <button onClick={() => del(c.id)} className="text-xs text-[var(--he-red)] underline">Suppr.</button>
          </div>
        ])}
      />
    </div>
  );
}

function TeachersAdmin({ data, reload }) {
  const empty = { first_name: "", disciplines: "", bio: "", photo: "", instagram: "" };
  const [form, setForm] = React.useState(empty);
  const [editing, setEditing] = React.useState(null);
  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));
  const startEdit = (t) => { setEditing(t.id); setForm({ ...t, disciplines: (t.disciplines || []).join(", ") }); };
  const cancel = () => { setEditing(null); setForm(empty); };
  const save = async (e) => {
    e.preventDefault();
    const body = { ...form, disciplines: form.disciplines.split(",").map(s => s.trim()).filter(Boolean) };
    try {
      if (editing) await http.put(`/admin/teachers/${editing}`, body);
      else await http.post("/admin/teachers", body);
      toast.success("Enregistré"); cancel(); reload();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };
  const del = async (id) => {
    if (!window.confirm("Supprimer ce professeur ?")) return;
    try { await http.delete(`/admin/teachers/${id}`); toast.success("Supprimé"); reload(); }
    catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };
  return (
    <div>
      <form onSubmit={save} className="grid grid-cols-1 md:grid-cols-5 gap-2 mb-6">
        <input placeholder="Prénom" value={form.first_name} onChange={set("first_name")} className={inp} required />
        <input placeholder="Disciplines (virgule)" value={form.disciplines} onChange={set("disciplines")} className={inp} />
        <input placeholder="Photo URL" value={form.photo} onChange={set("photo")} className={inp} />
        <input placeholder="Instagram URL" value={form.instagram} onChange={set("instagram")} className={inp} />
        <div className="flex gap-2">
          <button className="btn-red rounded-full px-4 py-2 text-xs flex-1">{editing ? "Modifier" : "Ajouter"}</button>
          {editing && <button type="button" onClick={cancel} className="rounded-full px-3 py-2 text-xs border border-black/15">Annuler</button>}
        </div>
        <textarea placeholder="Bio" value={form.bio} onChange={set("bio")} className={inp + " md:col-span-5"} rows={2} />
      </form>
      <Table
        cols={["Prénom", "Disciplines", "Bio", ""]}
        rows={data.map(t => [
          t.first_name, (t.disciplines || []).join(", "), (t.bio || "").slice(0, 80),
          <div key={`a-${t.id}`} className="flex gap-2">
            <button onClick={() => startEdit(t)} className="text-xs underline">Éditer</button>
            <button onClick={() => del(t.id)} className="text-xs text-[var(--he-red)] underline">Suppr.</button>
          </div>
        ])}
      />
    </div>
  );
}

function LocationsAdmin({ data, reload }) {
  const empty = { city: "", name: "", address: "", access: "" };
  const [form, setForm] = React.useState(empty);
  const [editing, setEditing] = React.useState(null);
  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));
  const startEdit = (l) => { setEditing(l.id); setForm({ ...empty, ...l }); };
  const cancel = () => { setEditing(null); setForm(empty); };
  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing) await http.put(`/admin/locations/${editing}`, form);
      else await http.post("/admin/locations", form);
      toast.success("Enregistré"); cancel(); reload();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };
  const del = async (id) => {
    if (!window.confirm("Supprimer ce lieu ?")) return;
    try { await http.delete(`/admin/locations/${id}`); toast.success("Supprimé"); reload(); }
    catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };
  return (
    <div>
      <form onSubmit={save} className="grid grid-cols-1 md:grid-cols-5 gap-2 mb-6">
        <input placeholder="Ville" value={form.city} onChange={set("city")} className={inp} required />
        <input placeholder="Nom" value={form.name} onChange={set("name")} className={inp} required />
        <input placeholder="Adresse" value={form.address} onChange={set("address")} className={inp} />
        <input placeholder="Accès" value={form.access} onChange={set("access")} className={inp} />
        <div className="flex gap-2">
          <button className="btn-red rounded-full px-4 py-2 text-xs flex-1">{editing ? "Modifier" : "Ajouter"}</button>
          {editing && <button type="button" onClick={cancel} className="rounded-full px-3 py-2 text-xs border border-black/15">Annuler</button>}
        </div>
      </form>
      <Table
        cols={["Ville", "Nom", "Adresse", ""]}
        rows={data.map(l => [l.city, l.name, l.address,
          <div key={`a-${l.id}`} className="flex gap-2">
            <button onClick={() => startEdit(l)} className="text-xs underline">Éditer</button>
            <button onClick={() => del(l.id)} className="text-xs text-[var(--he-red)] underline">Suppr.</button>
          </div>
        ])}
      />
    </div>
  );
}

function EventsAdmin({ data, reload }) {
  const empty = { name: "", category: "stage", date: "", end_date: "", time: "", location: "", description: "", speakers: "", price: "", image: "", booking_url: "", widget_url: "", featured: false, status: "open" };
  const [form, setForm] = React.useState(empty);
  const [editing, setEditing] = React.useState(null);
  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));
  const startEdit = (ev) => { setEditing(ev.id); setForm({ ...empty, ...ev }); };
  const cancel = () => { setEditing(null); setForm(empty); };
  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing) await http.put(`/admin/events/${editing}`, form);
      else await http.post("/admin/events", form);
      toast.success("Enregistré"); cancel(); reload();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };
  const del = async (id) => {
    if (!window.confirm("Supprimer cet événement ?")) return;
    try { await http.delete(`/admin/events/${id}`); toast.success("Supprimé"); reload(); }
    catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };
  return (
    <div>
      <form onSubmit={save} className="grid grid-cols-2 md:grid-cols-6 gap-2 mb-6">
        <input placeholder="Nom" value={form.name} onChange={set("name")} className={inp} required />
        <select value={form.category} onChange={set("category")} className={inp}>{["stage","soiree","spectacle","enfants","heelevents","compagnie"].map(c=><option key={c}>{c}</option>)}</select>
        <input placeholder="YYYY-MM-DD" value={form.date} onChange={set("date")} className={inp} required />
        <input placeholder="Date de fin (optionnel)" value={form.end_date || ""} onChange={set("end_date")} className={inp} />
        <input placeholder="Heure" value={form.time} onChange={set("time")} className={inp} />
        <input placeholder="Lieu" value={form.location} onChange={set("location")} className={inp} />
        <input placeholder="Tarif" value={form.price} onChange={set("price")} className={inp} />
        <input placeholder="Intervenants" value={form.speakers} onChange={set("speakers")} className={inp} />
        <input placeholder="Image URL" value={form.image} onChange={set("image")} className={inp + " md:col-span-2"} />
        <input placeholder="Lien réservation (https://…)" value={form.booking_url} onChange={set("booking_url")} className={inp + " md:col-span-2"} />
        <input placeholder="Widget HelloAsso bouton (URL /widget-bouton, optionnel)" value={form.widget_url || ""} onChange={set("widget_url")} className={inp} />
        <select value={form.status} onChange={set("status")} className={inp}>{["open","complet","termine"].map(s=><option key={s}>{s}</option>)}</select>
        <label className="flex items-center gap-2 text-xs font-bold px-1"><input type="checkbox" checked={!!form.featured} onChange={(e) => setForm(f => ({ ...f, featured: e.target.checked }))} /> À la une (accueil)</label>
        <div className="flex gap-2">
          <button className="btn-red rounded-full px-4 py-2 text-xs flex-1">{editing ? "Modifier" : "Ajouter"}</button>
          {editing && <button type="button" onClick={cancel} className="rounded-full px-3 py-2 text-xs border border-black/15">Annuler</button>}
        </div>
        <textarea placeholder="Description" value={form.description} onChange={set("description")} rows={2} className={inp + " md:col-span-6"} />
      </form>
      <Table
        cols={["Nom", "Catégorie", "Date", "Lieu", "Statut", "Résa", ""]}
        rows={data.map(ev => [ev.name, ev.category, ev.date, ev.location, ev.status, ev.booking_url ? "Oui" : "—",
          <div key={`a-${ev.id}`} className="flex gap-2">
            <button onClick={() => startEdit(ev)} className="text-xs underline">Éditer</button>
            <button onClick={() => del(ev.id)} className="text-xs text-[var(--he-red)] underline">Suppr.</button>
          </div>
        ])}
      />
    </div>
  );
}

function ReadOnlyList({ items, fields }) {
  if (!items.length) return <p className="text-sm text-neutral-500">Aucun enregistrement pour le moment.</p>;
  return (
    <div className="space-y-4">
      {items.map((it, i) => (
        <div key={i} className="p-4 border border-black/10 rounded-xl text-sm">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1">
            {fields.map(f => (
              <div key={f}>
                <span className="he-overline text-neutral-500">{f}</span>
                <div className="mt-0.5 break-words">{Array.isArray(it[f]) ? it[f].join(", ") : (it[f] || "—")}</div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function DisciplinesAdmin({ data, reload }) {
  const empty = { name: "", slug: "", tag: "afro", desc: "", tenue: "", bienfaits: "", active: true };
  const [form, setForm] = React.useState(empty);
  const [editing, setEditing] = React.useState(null);
  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));
  const startEdit = (d) => { setEditing(d.id); setForm({ ...empty, ...d }); };
  const cancel = () => { setEditing(null); setForm(empty); };
  const save = async (e) => {
    e.preventDefault();
    const body = { ...form, slug: form.slug || form.name.toLowerCase().replace(/['’\s]+/g, "-") };
    try {
      if (editing) await http.put(`/admin/disciplines/${editing}`, body);
      else await http.post("/admin/disciplines", body);
      toast.success("Enregistré"); cancel(); reload();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };
  const toggle = async (d) => {
    try { await http.put(`/admin/disciplines/${d.id}`, { ...d, active: !d.active }); reload(); }
    catch { toast.error("Erreur"); }
  };
  const del = async (id) => {
    if (!window.confirm("Supprimer cette discipline ?")) return;
    try { await http.delete(`/admin/disciplines/${id}`); toast.success("Supprimé"); reload(); }
    catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };
  return (
    <div>
      <PreviewHint to="/cours" label="Voir la page Cours" />
      <form onSubmit={save} className="grid grid-cols-1 md:grid-cols-6 gap-2 mb-6">
        <input placeholder="Nom" value={form.name} onChange={set("name")} className={inp} required />
        <input placeholder="Slug" value={form.slug} onChange={set("slug")} className={inp} />
        <select value={form.tag} onChange={set("tag")} className={inp}>{["afro","couple","urban","oriental","heels","fitness","kids"].map(t=><option key={t}>{t}</option>)}</select>
        <input placeholder="Description courte" value={form.desc} onChange={set("desc")} className={inp + " md:col-span-2"} />
        <div className="flex gap-2 items-center">
          <label className="flex items-center gap-1.5 text-xs"><input type="checkbox" checked={form.active} onChange={e => setForm(f => ({ ...f, active: e.target.checked }))} /> Active</label>
          <button className="btn-red rounded-full px-4 py-2 text-xs flex-1">{editing ? "Modifier" : "Ajouter"}</button>
          {editing && <button type="button" onClick={cancel} className="rounded-full px-3 py-2 text-xs border border-black/15">Annuler</button>}
        </div>
      </form>
      <Table
        cols={["Nom", "Tag", "Description", "Statut", ""]}
        rows={data.map(d => [
          d.name, d.tag, (d.desc || "").slice(0, 60),
          d.active ? <span className="text-xs font-bold text-green-600">Active</span> : <span className="text-xs font-bold text-neutral-400">Masquée</span>,
          <div key={`a-${d.id}`} className="flex gap-2 whitespace-nowrap">
            <button onClick={() => startEdit(d)} className="text-xs underline">Éditer</button>
            <button onClick={() => toggle(d)} className="text-xs underline">{d.active ? "Désactiver" : "Activer"}</button>
            <button onClick={() => del(d.id)} className="text-xs text-[var(--he-red)] underline">Suppr.</button>
          </div>
        ])}
      />
    </div>
  );
}

function FormulasAdmin({ data, reload }) {
  const empty = { slug: "", name: "", price: "", period: "par an", annual: true, insurance_price: "", total_price: "", cta: "", highlight: false, features: "", order: 0, payment_url: "" };
  const [form, setForm] = React.useState(empty);
  const [editing, setEditing] = React.useState(null);
  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));
  const startEdit = (f) => { setEditing(f.id); setForm({ ...empty, ...f, features: (f.features || []).join(", "), insurance_price: f.insurance_price || "", total_price: f.total_price || "" }); };
  const cancel = () => { setEditing(null); setForm(empty); };
  const save = async (e) => {
    e.preventDefault();
    const body = {
      ...form,
      features: form.features.split(",").map(s => s.trim()).filter(Boolean),
      insurance_price: form.annual && form.insurance_price ? form.insurance_price : null,
      total_price: form.annual && form.total_price ? form.total_price : null,
      order: Number(form.order) || 0,
    };
    try {
      if (editing) await http.put(`/admin/formulas/${editing}`, body);
      else await http.post("/admin/formulas", body);
      toast.success("Enregistré — visible immédiatement sur la page Tarifs"); cancel(); reload();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };
  const del = async (id) => {
    if (!window.confirm("Supprimer cette formule ?")) return;
    try { await http.delete(`/admin/formulas/${id}`); toast.success("Supprimé"); reload(); }
    catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };
  return (
    <div>
      <PreviewHint to="/tarifs" label="Voir la page Tarifs" />
      <form onSubmit={save} className="grid grid-cols-2 md:grid-cols-6 gap-2 mb-6">
        <input placeholder="Nom" value={form.name} onChange={set("name")} className={inp} required />
        <input placeholder="Slug" value={form.slug} onChange={set("slug")} className={inp} required />
        <input placeholder="Prix (ex: 270 €)" value={form.price} onChange={set("price")} className={inp} required />
        <input placeholder="Période (par an / 3 cours)" value={form.period} onChange={set("period")} className={inp} />
        <input placeholder="Assurance (ex: 20 €)" value={form.insurance_price} onChange={set("insurance_price")} className={inp} />
        <input placeholder="Total avec assurance" value={form.total_price} onChange={set("total_price")} className={inp} />
        <input placeholder="Bouton (ex: Choisir Essentiel)" value={form.cta} onChange={set("cta")} className={inp} />
        <input placeholder="Lien paiement (HelloAsso…)" value={form.payment_url || ""} onChange={set("payment_url")} className={inp} />
        <input placeholder="Ordre" type="number" value={form.order} onChange={set("order")} className={inp} />
        <label className="flex items-center gap-1.5 text-xs"><input type="checkbox" checked={form.annual} onChange={e => setForm(f => ({ ...f, annual: e.target.checked }))} /> Annuelle</label>
        <label className="flex items-center gap-1.5 text-xs"><input type="checkbox" checked={form.highlight} onChange={e => setForm(f => ({ ...f, highlight: e.target.checked }))} /> Mise en avant</label>
        <div className="flex gap-2 col-span-2">
          <button className="btn-red rounded-full px-4 py-2 text-xs flex-1">{editing ? "Modifier" : "Ajouter"}</button>
          {editing && <button type="button" onClick={cancel} className="rounded-full px-3 py-2 text-xs border border-black/15">Annuler</button>}
        </div>
        <input placeholder="Bénéfices (séparés par virgule)" value={form.features} onChange={set("features")} className={inp + " md:col-span-6"} />
      </form>
      <Table
        cols={["Nom", "Prix", "Assurance", "Total", "Avantages", ""]}
        rows={data.map(f => [
          f.name, f.price, f.insurance_price || "—", f.total_price || "—", (f.features || []).join(", ").slice(0, 60),
          <div key={`a-${f.id}`} className="flex gap-2 whitespace-nowrap">
            <button onClick={() => startEdit(f)} className="text-xs underline">Éditer</button>
            <button onClick={() => del(f.id)} className="text-xs text-[var(--he-red)] underline">Suppr.</button>
          </div>
        ])}
      />
    </div>
  );
}

function NewsAdmin({ data, reload }) {
  const empty = { title: "", text: "", date: new Date().toISOString().slice(0, 10), image: "", active: true };
  const [form, setForm] = React.useState(empty);
  const [editing, setEditing] = React.useState(null);
  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));
  const startEdit = (n) => { setEditing(n.id); setForm({ ...empty, ...n }); };
  const cancel = () => { setEditing(null); setForm(empty); };
  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing) await http.put(`/admin/news/${editing}`, form);
      else await http.post("/admin/news", form);
      toast.success("Enregistré"); cancel(); reload();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };
  const toggle = async (n) => {
    try { await http.put(`/admin/news/${n.id}`, { ...n, active: !n.active }); reload(); }
    catch { toast.error("Erreur"); }
  };
  const del = async (id) => {
    if (!window.confirm("Supprimer cette actualité ?")) return;
    try { await http.delete(`/admin/news/${id}`); toast.success("Supprimé"); reload(); }
    catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };
  return (
    <div>
      <PreviewHint to="/" label="Voir l’accueil (section Actualités)" />
      <form onSubmit={save} className="grid grid-cols-2 md:grid-cols-6 gap-2 mb-6">
        <input placeholder="Titre" value={form.title} onChange={set("title")} className={inp + " md:col-span-2"} required />
        <input placeholder="Date (YYYY-MM-DD)" value={form.date} onChange={set("date")} className={inp} />
        <input placeholder="Image URL" value={form.image} onChange={set("image")} className={inp + " md:col-span-2"} />
        <div className="flex gap-2 items-center">
          <label className="flex items-center gap-1.5 text-xs"><input type="checkbox" checked={form.active} onChange={e => setForm(f => ({ ...f, active: e.target.checked }))} /> Publiée</label>
          <button className="btn-red rounded-full px-4 py-2 text-xs flex-1">{editing ? "Modifier" : "Publier"}</button>
          {editing && <button type="button" onClick={cancel} className="rounded-full px-3 py-2 text-xs border border-black/15">Annuler</button>}
        </div>
        <textarea placeholder="Texte de l’actualité" value={form.text} onChange={set("text")} rows={2} className={inp + " md:col-span-6"} />
      </form>
      <Table
        cols={["Titre", "Date", "Statut", ""]}
        rows={data.map(n => [
          n.title, n.date,
          n.active ? <span className="text-xs font-bold text-green-600">Publiée</span> : <span className="text-xs font-bold text-neutral-400">Brouillon</span>,
          <div key={`a-${n.id}`} className="flex gap-2 whitespace-nowrap">
            <button onClick={() => startEdit(n)} className="text-xs underline">Éditer</button>
            <button onClick={() => toggle(n)} className="text-xs underline">{n.active ? "Dépublier" : "Publier"}</button>
            <button onClick={() => del(n.id)} className="text-xs text-[var(--he-red)] underline">Suppr.</button>
          </div>
        ])}
      />
    </div>
  );
}

const SETTING_IMAGES = [
  ["hero", "Image hero (accueil)"],
  ["afrolat-studio", "Visuel pôle Afrolat’Studio"],
  ["heels-compagnie", "Visuel pôle La Compagnie"],
  ["solidarite", "Visuel pôle Solidarité"],
  ["entree-des-artistes", "Visuel pôle L’Entrée des Artistes"],
  ["city_fresnes", "Carte ville Fresnes"],
  ["city_lhay", "Carte ville L’Haÿ-les-Roses"],
  ["city_chevilly", "Carte ville Chevilly-Larue"],
];
const SETTING_TEXTS = [
  ["hero_sub", "Sous-titre du hero (accueil)"],
  ["home_intro", "Phrase d’introduction (accueil)"],
  ["asso_intro", "Texte « Qui sommes-nous ? » (page Association)"],
];

function SettingsAdmin({ settings, reload }) {
  const [texts, setTexts] = React.useState(settings.texts || {});
  const [images, setImages] = React.useState(settings.images || {});
  React.useEffect(() => { setTexts(settings.texts || {}); setImages(settings.images || {}); }, [settings]);
  const [saving, setSaving] = React.useState(false);
  const save = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      await http.put("/admin/settings", { texts, images });
      toast.success("Textes & visuels mis à jour partout sur le site"); reload();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };
  return (
    <form onSubmit={save} className="space-y-10">
      <PreviewHint to="/" label="Voir l’accueil" />
      <section>
        <h3 className="font-heading text-xl font-bold mb-4">Textes du site</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {SETTING_TEXTS.map(([k, label]) => (
            <label key={k} className="flex flex-col">
              <span className="he-overline text-neutral-500 mb-1.5">{label}</span>
              <input value={texts[k] || ""} onChange={e => setTexts(t => ({ ...t, [k]: e.target.value }))} className={inp} />
            </label>
          ))}
        </div>
      </section>
      <section>
        <h3 className="font-heading text-xl font-bold mb-4">Photos & visuels</h3>
        <p className="text-xs text-neutral-500 mb-4">Collez l’URL d’une image. L’aperçu s’affiche immédiatement, et le changement est répercuté partout où le visuel apparaît après enregistrement.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {SETTING_IMAGES.map(([k, label]) => (
            <div key={k} className="border border-black/10 rounded-xl p-3">
              <span className="he-overline text-neutral-500 block mb-2">{label}</span>
              <input value={images[k] || ""} onChange={e => setImages(im => ({ ...im, [k]: e.target.value }))} className={inp} placeholder="https://…" />
              {images[k] ? <img src={images[k]} alt={label} className="mt-2 h-28 w-full object-cover rounded-lg" /> : null}
            </div>
          ))}
        </div>
      </section>
      <button data-testid={ADMIN.settingsSave} disabled={saving} className="btn-red rounded-full px-8 py-3 text-sm disabled:opacity-60">
        {saving ? "Enregistrement..." : "Enregistrer et publier"}
      </button>
    </form>
  );
}

function PartnersAdmin({ data, reload }) {
  const empty = { name: "", logo_url: "", website: "", active: true };
  const [form, setForm] = React.useState(empty);
  const [editing, setEditing] = React.useState(null);
  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));
  const startEdit = (p) => { setEditing(p.id); setForm({ ...empty, ...p }); };
  const cancel = () => { setEditing(null); setForm(empty); };
  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing) await http.put(`/admin/partners/${editing}`, form);
      else await http.post("/admin/partners", form);
      toast.success("Enregistré"); cancel(); reload();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };
  const toggle = async (p) => {
    try { await http.put(`/admin/partners/${p.id}`, { ...p, active: !p.active }); reload(); }
    catch { toast.error("Erreur"); }
  };
  const del = async (id) => {
    if (!window.confirm("Supprimer ce partenaire ?")) return;
    try { await http.delete(`/admin/partners/${id}`); toast.success("Supprimé"); reload(); }
    catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };
  return (
    <div>
      <PreviewHint to="/" label="Voir l’accueil (section « Ils nous ont fait confiance »)" />
      <form onSubmit={save} className="grid grid-cols-1 md:grid-cols-5 gap-2 mb-6">
        <input placeholder="Nom du partenaire" value={form.name} onChange={set("name")} className={inp} required />
        <input placeholder="Logo URL" value={form.logo_url} onChange={set("logo_url")} className={inp} />
        <input placeholder="Site web (optionnel)" value={form.website} onChange={set("website")} className={inp} />
        <label className="flex items-center gap-1.5 text-xs"><input type="checkbox" checked={form.active} onChange={e => setForm(f => ({ ...f, active: e.target.checked }))} /> Visible</label>
        <div className="flex gap-2">
          <button className="btn-red rounded-full px-4 py-2 text-xs flex-1">{editing ? "Modifier" : "Ajouter"}</button>
          {editing && <button type="button" onClick={cancel} className="rounded-full px-3 py-2 text-xs border border-black/15">Annuler</button>}
        </div>
        {form.logo_url ? <img src={form.logo_url} alt="aperçu" className="h-12 object-contain md:col-span-5" /> : null}
      </form>
      <Table
        cols={["Nom", "Logo", "Statut", ""]}
        rows={data.map(p => [
          p.name,
          p.logo_url ? <img key={`l-${p.id}`} src={p.logo_url} alt={p.name} className="h-8 object-contain" /> : "—",
          p.active ? <span className="text-xs font-bold text-green-600">Visible</span> : <span className="text-xs font-bold text-neutral-400">Masqué</span>,
          <div key={`a-${p.id}`} className="flex gap-2 whitespace-nowrap">
            <button onClick={() => startEdit(p)} className="text-xs underline">Éditer</button>
            <button onClick={() => toggle(p)} className="text-xs underline">{p.active ? "Masquer" : "Afficher"}</button>
            <button onClick={() => del(p.id)} className="text-xs text-[var(--he-red)] underline">Suppr.</button>
          </div>
        ])}
      />
    </div>
  );
}

function GalleryAdmin({ data, reload }) {
  const empty = { title: "", kind: "photo", url: "", pole: "", group: "", active: true };
  const [form, setForm] = React.useState(empty);
  const [editing, setEditing] = React.useState(null);
  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));
  const startEdit = (g) => { setEditing(g.id); setForm({ ...empty, ...g }); };
  const cancel = () => { setEditing(null); setForm(empty); };
  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing) await http.put(`/admin/gallery/${editing}`, form);
      else await http.post("/admin/gallery", form);
      toast.success("Enregistré"); cancel(); reload();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };
  const toggle = async (g) => {
    try { await http.put(`/admin/gallery/${g.id}`, { ...g, active: !g.active }); reload(); }
    catch { toast.error("Erreur"); }
  };
  const del = async (id) => {
    if (!window.confirm("Supprimer cet élément ?")) return;
    try { await http.delete(`/admin/gallery/${id}`); toast.success("Supprimé"); reload(); }
    catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };
  return (
    <div>
      <PreviewHint to="/galerie" label="Voir la page Galerie" />
      <form onSubmit={save} className="grid grid-cols-2 md:grid-cols-7 gap-2 mb-6">
        <input placeholder="Titre" value={form.title} onChange={set("title")} className={inp} required />
        <select value={form.kind} onChange={set("kind")} className={inp}>
          <option value="photo">Photo</option>
          <option value="video">Vidéo</option>
        </select>
        <input placeholder="URL image ou vidéo (YouTube accepté)" value={form.url} onChange={set("url")} className={inp + " md:col-span-2"} required />
        <input placeholder="Pôle (optionnel)" value={form.pole} onChange={set("pole")} className={inp} />
        <input placeholder="Groupe / événement (optionnel)" value={form.group || ""} onChange={set("group")} className={inp} />
        <div className="flex gap-2 items-center">
          <label className="flex items-center gap-1.5 text-xs"><input type="checkbox" checked={form.active} onChange={e => setForm(f => ({ ...f, active: e.target.checked }))} /> Visible</label>
          <button className="btn-red rounded-full px-4 py-2 text-xs flex-1">{editing ? "Modifier" : "Ajouter"}</button>
          {editing && <button type="button" onClick={cancel} className="rounded-full px-3 py-2 text-xs border border-black/15">Annuler</button>}
        </div>
        {form.url && form.kind === "photo" ? <img src={form.url} alt="aperçu" className="h-16 object-cover rounded-lg md:col-span-6" /> : null}
      </form>
      <Table
        cols={["Titre", "Type", "Aperçu", "Statut", ""]}
        rows={data.map(g => [
          <span key={`t-${g.id}`}>{g.title}{g.group ? <span className="block text-xs text-neutral-500">{g.group}</span> : null}</span>, g.kind,
          g.kind === "photo" && g.url ? <img key={`p-${g.id}`} src={g.url} alt={g.title} className="h-10 w-14 object-cover rounded" /> : g.url.slice(0, 40),
          g.active ? <span className="text-xs font-bold text-green-600">Visible</span> : <span className="text-xs font-bold text-neutral-400">Masqué</span>,
          <div key={`a-${g.id}`} className="flex gap-2 whitespace-nowrap">
            <button onClick={() => startEdit(g)} className="text-xs underline">Éditer</button>
            <button onClick={() => toggle(g)} className="text-xs underline">{g.active ? "Masquer" : "Afficher"}</button>
            <button onClick={() => del(g.id)} className="text-xs text-[var(--he-red)] underline">Suppr.</button>
          </div>
        ])}
      />
    </div>
  );
}

function PreviewHint({ to, label }) {
  return (
    <div className="mb-5 text-xs text-neutral-500 flex items-center gap-2">
      <span>Astuce : enregistrez puis vérifiez le rendu en direct →</span>
      <a href={to} target="_blank" rel="noreferrer" className="underline hover:text-[var(--he-red)]">{label}</a>
    </div>
  );
}

function Table({ cols, rows }) {
  return (
    <div className="overflow-x-auto border border-black/10 rounded-xl">
      <table className="w-full text-sm">
        <thead className="bg-neutral-50 text-left text-xs uppercase tracking-wider">
          <tr>{cols.map((c, i) => <th key={i} className="p-3 font-bold">{c}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} className="border-t border-black/5">
              {row.map((cell, j) => <td key={j} className="p-3 align-top">{cell}</td>)}
            </tr>
          ))}
          {!rows.length && <tr><td colSpan={cols.length} className="p-6 text-center text-neutral-500">Aucun élément.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}
